import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FeatureFlagDeleteResponse,
  FeatureFlagItem,
  FeatureFlagListResponse,
  FeatureFlagUpdateRequest,
  FeatureFlagUpdateResponse,
  featureFlagApi,
} from '../../../lib/api/featureFlag';
import { featureFlagQuery } from '../queries';

type UpdateVariables = { ticketKey: string; body: FeatureFlagUpdateRequest };
type DeleteVariables = { ticketKey: string; reason: string };

/**
 * 응답의 한 줄로 목록 캐시를 고친다 — 다시 조회하지 않아도 화면이 바로 바뀐다.
 * flag 가 null(코드에 없는 키를 지움)이면 목록에서 뺀다.
 */
const useApplyToList = () => {
  const queryClient = useQueryClient();
  return async (ticketKey: string, flag: FeatureFlagItem | null): Promise<void> => {
    // 🔴 진행 중인 조회를 먼저 끊는다 — 저장 전 값을 읽은 GET 이 나중에 도착하면 방금 바꾼 값을 덮는다
    await queryClient.cancelQueries({ queryKey: featureFlagQuery.list().queryKey });
    queryClient.setQueryData<FeatureFlagListResponse>(featureFlagQuery.list().queryKey, (previous) =>
      previous
        ? {
            ...previous,
            flags: flag
              ? previous.flags.map((item) => (item.ticketKey === ticketKey ? flag : item))
              : previous.flags.filter((item) => item.ticketKey !== ticketKey),
          }
        : previous,
    );
    // 이력은 값과 같은 트랜잭션으로 쌓인다 — 탭을 열면 바로 보이게 무효화만 해 둔다
    void queryClient.invalidateQueries({ queryKey: featureFlagQuery.histories._def });
  };
};

export const useUpdateFeatureFlagMutation = () => {
  const applyToList = useApplyToList();
  return useMutation<FeatureFlagUpdateResponse, Error, UpdateVariables>({
    mutationFn: ({ ticketKey, body }) => featureFlagApi.updateFlag(ticketKey, body),
    onSuccess: (response, { ticketKey }) => applyToList(ticketKey, response.flag),
  });
};

export const useDeleteFeatureFlagMutation = () => {
  const applyToList = useApplyToList();
  return useMutation<FeatureFlagDeleteResponse, Error, DeleteVariables>({
    mutationFn: ({ ticketKey, reason }) => featureFlagApi.deleteFlag(ticketKey, reason),
    onSuccess: (response, { ticketKey }) => applyToList(ticketKey, response.flag),
  });
};
